/**
 * Super-admin dialog for choosing which modules an account has purchased.
 * Saves the checked set as the account's exact entitlement list, and makes a
 * revocation that would strip modules from teams confirm what it will tear
 * down before the write is issued.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";

interface AccountEntitlementsDialogProps {
	account: { id: string; name: string } | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

interface PendingRevocation {
	name: string;
	assignedTeamCount: number;
}

/**
 * Dialog for granting and revoking an account's module entitlements.
 * @param props - component props
 * @param props.account - the account being edited, or null when closed
 * @param props.open - whether the dialog is open
 * @param props.onOpenChange - callback when the open state changes
 * @returns the entitlement management dialog
 */
export function AccountEntitlementsDialog({
	account,
	open,
	onOpenChange,
}: AccountEntitlementsDialogProps) {
	const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
	const [pendingRevocations, setPendingRevocations] = useState<PendingRevocation[] | null>(null);

	const utils = trpc.useUtils();

	const entitlementsQuery = trpc.entitlements.getWithModules.useQuery(
		{ accountId: account?.id ?? "" },
		{ enabled: open && !!account }
	);

	const setMutation = trpc.entitlements.set.useMutation({
		onSuccess: () => {
			void utils.entitlements.getWithModules.invalidate();
			void utils.accounts.getTeamModules.invalidate();
			void utils.modules.list.invalidate();
			setPendingRevocations(null);
			onOpenChange(false);
		},
	});

	const modules = entitlementsQuery.data?.modules ?? [];

	// Seed the checkboxes from the account's stored entitlements each time the
	// dialog opens, so a cancelled edit never leaks into the next one.
	useEffect(() => {
		if (open && entitlementsQuery.data) {
			setSelectedSlugs(
				new Set(entitlementsQuery.data.modules.filter((m) => m.entitled).map((m) => m.slug))
			);
			setPendingRevocations(null);
		}
	}, [open, entitlementsQuery.data]);

	/**
	 * Toggle a module in the pending entitlement selection.
	 * @param slug - the module slug toggled
	 * @param checked - whether it is now selected
	 */
	const toggleSlug = (slug: string, checked: boolean) => {
		setSelectedSlugs((prev) => {
			const next = new Set(prev);
			if (checked) next.add(slug);
			else next.delete(slug);
			return next;
		});
	};

	/** Persist the current selection as the account's exact entitlement set. */
	const persist = () => {
		if (!account) return;
		setMutation.mutate({ accountId: account.id, moduleSlugs: [...selectedSlugs] });
	};

	/**
	 * Save, first confirming any revocation that would strip a module from teams
	 * that currently have it assigned.
	 */
	const handleSave = () => {
		const revocations = modules
			.filter((m) => m.entitled && !selectedSlugs.has(m.slug) && m.assignedTeamCount > 0)
			.map((m) => ({ name: m.name, assignedTeamCount: m.assignedTeamCount }));

		if (revocations.length > 0) {
			setPendingRevocations(revocations);
			return;
		}
		persist();
	};

	const isBusy = setMutation.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				{pendingRevocations ? (
					<form onSubmit={(e) => { e.preventDefault(); persist(); }}>
						<DialogHeader>
							<DialogTitle>Confirm removing access</DialogTitle>
						</DialogHeader>
						<div className="space-y-3 py-2">
							{pendingRevocations.map((revocation) => (
								<p key={revocation.name} className="text-sm">
									Removing {revocation.name} will also revoke it from{" "}
									{revocation.assignedTeamCount} team(s) that currently have it assigned.
									This cannot be undone.
								</p>
							))}
						</div>
						{setMutation.isError && (
							<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
								{setMutation.error.message}
							</div>
						)}
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => { setPendingRevocations(null); }}
								disabled={isBusy}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isBusy}>
								{isBusy ? "Saving..." : "Remove and Save"}
							</Button>
						</DialogFooter>
					</form>
				) : (
					<form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
						<DialogHeader>
							<DialogTitle>Module Entitlements</DialogTitle>
							<DialogDescription>
								Select which modules {account?.name ?? ""} has purchased. Teams can only be
								assigned modules their account is entitled to.
							</DialogDescription>
						</DialogHeader>

						<div className="max-h-80 space-y-3 overflow-y-auto py-2">
							{entitlementsQuery.isLoading ? (
								<div className="flex items-center justify-center py-6">
									<div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
								</div>
							) : entitlementsQuery.error ? (
								<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
									{entitlementsQuery.error.message}
								</div>
							) : modules.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									No enabled modules exist on the platform. Enable a module in Admin →
									Modules first.
								</p>
							) : (
								<>
									{selectedSlugs.size === 0 && (
										<div className="rounded-md border border-amber-800 bg-amber-950 p-3 text-sm text-amber-200">
											This account is entitled to no modules. Its teams cannot be assigned
											anything until you grant at least one entitlement here.
										</div>
									)}
									{modules.map((mod) => (
										<div key={mod.slug} className="flex items-start gap-2">
											<Checkbox
												id={`entitlement-${mod.slug}`}
												className="mt-1"
												checked={selectedSlugs.has(mod.slug)}
												onCheckedChange={(checked: boolean) => {
													toggleSlug(mod.slug, checked);
												}}
											/>
											<Label
												htmlFor={`entitlement-${mod.slug}`}
												className="font-normal leading-snug"
											>
												{mod.name}
												{mod.description && (
													<span className="block text-xs text-muted-foreground">
														{mod.description}
													</span>
												)}
											</Label>
										</div>
									))}
								</>
							)}
						</div>

						{setMutation.isError && (
							<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
								{setMutation.error.message}
							</div>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => { onOpenChange(false); }}
								disabled={isBusy}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isBusy || entitlementsQuery.isLoading}>
								{isBusy ? "Saving..." : "Save Entitlements"}
							</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
